
import React, { useState, useEffect, useCallback } from "react";
import {
    Button,
    Col,
    Form,
    FormControl,
    FormGroup,
    FormLabel,
    FormSelect,
    Modal,
    Pagination,
    Row
} from "react-bootstrap";
import SearchIcon from "../../../images/search-icon.svg";
import ViewIcon from "../../../images/view-icon.svg";
import EditIcon from "../../../images/edit-icon.svg";
import DeleteIcon from "../../../images/delete-icon.svg";
import { InputGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from 'axios';
import apiConfig from "../apiConfig.ts";
import { HiOutlineDownload, HiPlus } from 'react-icons/hi';
import { IoCopyOutline, IoCheckmarkOutline } from 'react-icons/io5';
import ExportModal from "../../ExportModal";
import DatePicker, { DateObject } from "react-multi-date-picker";
import CalenderIcon from "../../../images/calender-icon.svg";
import "./ReferralDiscount.scss";
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Validation schema
const promoCodeSchema = Yup.object({
    title: Yup.string()
        .trim()
        .required('Coupon name is required')
        .min(3, 'Coupon name must be at least 3 characters'),

    code: Yup.string()
        .required('Coupon code is required')
        .min(6, 'Code must be at least 6 characters')
        .matches(/^[A-Z0-9]+$/, 'Code must contain only uppercase letters and numbers'),

    discountType: Yup.string()
        .oneOf(['percentage', 'fixed'], 'Invalid discount type')
        .required('Discount type is required'),

    discount: Yup.string()
        .required('Discount value is required'),

    planType: Yup.string()
        .oneOf(['monthly', 'yearly'], 'Invalid plan type')
        .required('Plan type is required'),

    usersAllowed: Yup.string()
        .required('User limit is required'),

    startDate: Yup.date()
        .required('Start date is required'),

    expiryDate: Yup.date()
        .required('Expiry date is required')
        .min(Yup.ref('startDate'), 'Expiry date must be after start date'),

    status: Yup.string()
        .oneOf(['active', 'inactive'], 'Invalid status')
        .required('Status is required'),

    multipleUse: Yup.boolean()
});

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
    };
};

const promoCodeApi = {
    getAllPromoCodes: async ({ page = 1, limit = 10, search = '', status = '' } = {}) => {
        try {
            const params: any = { page, limit };
            if (search) params.search = search;
            if (status) params.status = status;
            const response = await axios.get(`${apiConfig.apiBaseURL}/promo/get-all-promo-codes`, {
                headers: getAuthHeaders(),
                params
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    createPromoCode: async (data) => {
        try {
            const response = await axios.post(`${apiConfig.apiBaseURL}/promo/create-promo-code`, data, {
                headers: getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    togglePromoCodeStatus: async (data) => {
        try {
            const response = await axios.post(`${apiConfig.apiBaseURL}/promo/toggle-promo-code-status`, data, {
                headers: getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    softDeleteExpiredPromoCode: async (data) => {
        try {
            const response = await axios.post(`${apiConfig.apiBaseURL}/promo/soft-delete-expired-promo-code`, data, {
                headers: getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getGiftTransactions: async (code) => {
        try {
            const response = await axios.get(`${apiConfig.apiBaseURL}/promo/gift-transactions/${code}`, {
                headers: getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
};

const ReferralDiscount = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState(""); // All, active, upcoming, expired
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("add"); // "add" or "edit"
    const [editData, setEditData] = useState(null);
    const [promoCodes, setPromoCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [copiedCode, setCopiedCode] = useState<string | null>(null);

    // Formik form handling
    const formik = useFormik({
        initialValues: {
            title: '',
            code: '',
            discountType: 'percentage',
            discount: '',
            planType: 'monthly',
            usersAllowed: '',
            startDate: '',
            expiryDate: '',
            status: 'active',
            multipleUse: true
        },
        validationSchema: promoCodeSchema,
        enableReinitialize: true,
        validateOnMount: false,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                if (modalMode === "add") {
                    await promoCodeApi.createPromoCode(values);
                    toast.success('Promo code created successfully');
                } else {
                    // Update only the active status
                    await promoCodeApi.togglePromoCodeStatus({
                        code: values.code,
                        active: values.status === 'active'
                    });
                    toast.success('Promo code status updated successfully');
                }
                setShowModal(false);
                formik.resetForm();
                setCurrentPage(1);
                fetchPromoCodes(1, itemsPerPage, searchTerm, statusFilter);
            } catch (error) {
                toast.error(error.message || 'Error saving promo code');
            } finally {
                setLoading(false);
            }
        }
    });

    const fetchPromoCodes = async (page = 1, limit = itemsPerPage, search = searchTerm, status = statusFilter) => {
        try {
            setLoading(true);
            const response = await promoCodeApi.getAllPromoCodes({ page, limit, search, status });
            setPromoCodes(response.data || []);
            if (response.pagination) {
                setTotalCount(response.pagination.totalCount);
                setTotalPages(response.pagination.totalPages);
            }
        } catch (error) {
            toast.error('Failed to fetch promo codes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPromoCodes(1, itemsPerPage, '', '');
    }, []);

    const handleAddNew = () => {
        setModalMode("add");
        setEditData(null);
        formik.resetForm();
        setShowModal(true);
    };

    // Handle export
    const handleExport = () => {
        setShowExportModal(true);
    };

    const handleExportExcel = () => {
        toast.info('Exporting to Excel...');
        // TODO: Implement Excel export logic
    };

    const handleExportPdf = () => {
        toast.info('Exporting to PDF...');
        // TODO: Implement PDF export logic
    };

    const handleEditClick = (rowData) => {
        setModalMode("edit");
        setEditData(rowData);
        formik.setValues({
            title: rowData.couponName || '',
            code: rowData.couponCode || '',
            discountType: rowData.discountType || 'percentage',
            discount: rowData.discount?.replace('%', '') || '',
            planType: rowData.planType || 'monthly',
            usersAllowed: rowData.usersLimit || '',
            startDate: rowData.startDate ? rowData.startDate.split('T')[0] : '',
            expiryDate: rowData.endDate ? rowData.endDate.split('T')[0] : '',
            status: rowData.active ? 'active' : 'inactive',
            multipleUse: rowData.multipleUse || true
        });
        setShowModal(true);
    };

    const handleFormChange = (field, value) => {
        if (field === 'code') {
            // Convert to uppercase, only letters and numbers
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        }
        formik.setFieldValue(field, value);
    };

    const handleToggleStatus = async (promoCode) => {
        // Check if status is "upcoming" (future date)
        if (promoCode.status === 'upcoming') {
            Swal.fire({
                title: 'Cannot Activate',
                text: 'It is not possible to activate promo codes for future dates.',
                icon: 'warning',
                confirmButtonColor: '#3957a0',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Check if status is "expired"
        if (promoCode.status === 'expired') {
            Swal.fire({
                title: 'Cannot Change Status',
                text: 'Expired promo codes cannot be activated or modified.',
                icon: 'warning',
                confirmButtonColor: '#3957a0',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Determine current active state from either 'active' field or 'status' field
        const currentlyActive = promoCode.active === true || promoCode.status === 'active';
        const newActiveState = !currentlyActive;
        const newStatus = newActiveState ? 'active' : 'inactive';

        const result = await Swal.fire({
            title: 'Change Status?',
            text: `Are you sure you want to change the status of "${promoCode.couponName}" (${promoCode.couponCode}) to ${newStatus}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3957a0',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Change Status',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                await promoCodeApi.togglePromoCodeStatus({
                    code: promoCode.couponCode,
                    active: newActiveState
                });
                toast.success(`Promo code status updated to ${newStatus}`);
                fetchPromoCodes(currentPage, itemsPerPage, searchTerm, statusFilter);
            } catch (error) {
                toast.error(error.message || 'Failed to toggle promo code status');
            }
        }
    };

    const handleDelete = async (promoCode) => {
        const result = await Swal.fire({
            title: 'Delete Promo Code?',
            text: `Are you sure you want to delete "${promoCode.couponName}" (${promoCode.couponCode})? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                await promoCodeApi.softDeleteExpiredPromoCode({ code: promoCode.couponCode });
                toast.success('Promo code deleted successfully');
                fetchPromoCodes(currentPage, itemsPerPage, searchTerm, statusFilter);
            } catch (error) {
                toast.error(error.message || 'Failed to delete promo code');
            }
        }
    };

    return (
        <>
            <section className="main-body">
                <div className="management-sec">
                    <div className="management-sec-in">
                        

                        <div className="main-table">
                            <div className="tab-content tab-body">
                                <div className="referral-discount mb-3">
                                    {/* Filter and Search Row */}
                                    <div className="mb-3 main-table-filter">
                                        <div className="row g-2 align-items-stretch">
                                            {/* Search Field */}
                                            <div className='col-lg-3 col-md-6 col-12'>
                                                <div className='main-table-search h-100'>
                                                    <FormGroup className='form-group search-box mb-0 h-100'>
                                                        <InputGroup className="h-100">
                                                            <FormControl
                                                                type="text"
                                                                placeholder="Search promo codes..."
                                                                value={searchTerm}
                                                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); fetchPromoCodes(1, itemsPerPage, e.target.value, statusFilter); }}
                                                                className="h-100"
                                                            />
                                                            <InputGroup.Text>
                                                                <img src={SearchIcon} alt='' />
                                                            </InputGroup.Text>
                                                        </InputGroup>
                                                    </FormGroup>
                                                </div>
                                            </div>

                                            {/* Filter Dropdown */}
                                            <div className='col-lg-2 col-md-6 col-12'>
                                                <div className='select-field h-100'>
                                                    <FormGroup className='form-group mb-0 h-100'>
                                                        <Form.Select
                                                            value={statusFilter}
                                                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); fetchPromoCodes(1, itemsPerPage, searchTerm, e.target.value); }}
                                                            className="h-100"
                                                        >
                                                            <option value="">All</option>
                                                            <option value="active">Active</option>
                                                            <option value="upcoming">Upcoming</option>
                                                            <option value="expired">Expired</option>
                                                        </Form.Select>
                                                    </FormGroup>
                                                </div>
                                            </div>

                                            {/* Spacer to push add new button to the right */}
                                            <div className='col-lg-5 d-none d-lg-block'></div>

                                            {/* Add New Button */}
                                            <div className='col-lg-2 col-md-6 col-12'>
                                                <Button
                                                    variant="primary"
                                                    onClick={handleAddNew}
                                                    className="d-flex align-items-center justify-content-center gap-2 h-100 w-100"
                                                >
                                                    <HiPlus size={20} />
                                                    <span>Add New</span>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Code/Name</th>
                                                <th>Code</th>
                                                <th>Discount</th>
                                                <th>Start Date</th>
                                                <th>End Date</th>
                                                <th>Usage Limit</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={8} className="text-center">Loading...</td>
                                                </tr>
                                            ) : promoCodes.length === 0 ? (
                                                <tr>
                                                    <td colSpan={8} className="text-center">No promo codes found</td>
                                                </tr>
                                            ) : promoCodes.map((row) => (
                                                <tr key={row.id || row._id || row.couponCode}>
                                                    <td>{row.couponName}</td>
                                                    <td>
                                                        <span className="d-flex align-items-center gap-2">
                                                            <span
                                                                title={copiedCode === row.couponCode ? 'Copied!' : 'Copy code'}
                                                                style={{ cursor: 'pointer', color: copiedCode === row.couponCode ? '#28a745' : '#3957A0', flexShrink: 0 }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigator.clipboard.writeText(row.couponCode);
                                                                    setCopiedCode(row.couponCode);
                                                                    toast.success(`Code "${row.couponCode}" copied!`);
                                                                    setTimeout(() => setCopiedCode(null), 2000);
                                                                }}
                                                            >
                                                                {copiedCode === row.couponCode
                                                                    ? <IoCheckmarkOutline size={15} />
                                                                    : <IoCopyOutline size={15} />
                                                                }
                                                            </span>
                                                            {row.couponCode}
                                                        </span>
                                                    </td>
                                                    <td>{row.discount}</td>
                                                    <td>{row.startDate ? new Date(row.startDate).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit'
                                                    }) : '-'}</td>
                                                    <td>{row.endDate ? new Date(row.endDate).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit'
                                                    }) : '-'}</td>
                                                    <td>
                                                        <b style={{ color: "rgba(57, 87, 160, 1)" }}>{row.usersLimit}</b>
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`badge bg-${
                                                                row.status === 'active' ? "succ-badge" :
                                                                row.status === 'upcoming' ? "or-badge" :
                                                                row.status === 'expired' ? "gre-badge" :
                                                                "gre-badge"
                                                            }`}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => handleToggleStatus(row)}
                                                            title="Click to toggle status"
                                                        >
                                                            {row.status === 'active' ? "Active" :
                                                             row.status === 'upcoming' ? "Upcoming" :
                                                             row.status === 'expired' ? "Expired" :
                                                             "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2 table-options align-items-center">
                                                            <Link to={`/discount-codes-details/${row.id || row._id || row.couponCode}`}>
                                                                <img src={ViewIcon} alt="view" style={{ cursor: "pointer" }} />
                                                            </Link>
                                                            <Form.Check
                                                                type="switch"
                                                                id={`status-toggle-${row.id || row.couponCode}`}
                                                                checked={row.active || row.status === 'active'}
                                                                onChange={() => handleToggleStatus(row)}
                                                                title="Toggle active status"
                                                            />
                                                            <img
                                                                src={DeleteIcon}
                                                                alt="delete"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => handleDelete(row)}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {!loading && totalCount > 0 && (
                                    <div className="orders-pagination-wrapper">
                                        <div className="pagination-info-section">
                                            <div className="pagination-showing">
                                                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
                                            </div>
                                        </div>

                                        <div className="pagination-controls-section">
                                            <div className="pagination-per-page">
                                                <span className="pagination-label">Show</span>
                                                <select
                                                    className="form-select form-select-sm pagination-select"
                                                    value={itemsPerPage}
                                                    onChange={(e) => {
                                                        const newLimit = Number(e.target.value);
                                                        setItemsPerPage(newLimit);
                                                        setCurrentPage(1);
                                                        fetchPromoCodes(1, newLimit, searchTerm, statusFilter);
                                                    }}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                </select>
                                            </div>

                                            {totalPages > 1 && (
                                                <Pagination className="mb-0 orders-pagination">
                                                    <Pagination.Prev
                                                        disabled={currentPage === 1}
                                                        onClick={() => { setCurrentPage(currentPage - 1); fetchPromoCodes(currentPage - 1, itemsPerPage, searchTerm, statusFilter); }}
                                                    />
                                                    {Array.from({ length: Math.min(totalPages, 4) }, (_, index) => {
                                                        let pageNumber;
                                                        if (totalPages <= 4) {
                                                            pageNumber = index + 1;
                                                        } else if (currentPage <= 2) {
                                                            pageNumber = index < 2 ? index + 1 : totalPages - (3 - index);
                                                        } else if (currentPage >= totalPages - 1) {
                                                            pageNumber = index < 2 ? index + 1 : totalPages - (3 - index);
                                                        } else {
                                                            pageNumber = currentPage - 1 + index;
                                                        }
                                                        const showEllipsis = totalPages > 4 &&
                                                            ((index === 1 && currentPage > 3) ||
                                                             (index === 2 && currentPage < totalPages - 2));
                                                        return (
                                                            <React.Fragment key={index}>
                                                                {showEllipsis && <span className="px-2">...</span>}
                                                                <Pagination.Item
                                                                    active={pageNumber === currentPage}
                                                                    onClick={() => { setCurrentPage(pageNumber); fetchPromoCodes(pageNumber, itemsPerPage, searchTerm, statusFilter); }}
                                                                >
                                                                    {pageNumber}
                                                                </Pagination.Item>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                    <Pagination.Next
                                                        disabled={currentPage === totalPages}
                                                        onClick={() => { setCurrentPage(currentPage + 1); fetchPromoCodes(currentPage + 1, itemsPerPage, searchTerm, statusFilter); }}
                                                    />
                                                </Pagination>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Modal for Add/Edit */}
            <Modal
                className="main-modal"
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
            >
                <Modal.Header closeButton className="px-0">
                    <Modal.Title>
                        {modalMode === "edit" ? "Edit Code" : "Add New Code"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-0">
                    <Form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <FormLabel>Coupon Name {modalMode === "edit" && <span className="text-muted small">(Read-only)</span>}</FormLabel>
                                    <FormControl
                                        type="text"
                                        placeholder="e.g., Summer Sale"
                                        name="title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={modalMode === "edit"}
                                        isInvalid={formik.touched.title && !!formik.errors.title}
                                        style={modalMode === "edit" ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed', opacity: 0.7 } : {}}
                                    />
                                    {formik.touched.title && formik.errors.title && (
                                        <small className="text-danger">{formik.errors.title}</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <FormLabel>Coupon Code {modalMode === "edit" && <span className="text-muted small">(Read-only)</span>}</FormLabel>
                                    <FormControl
                                        type="text"
                                        placeholder="e.g., SAVE20"
                                        name="code"
                                        value={formik.values.code}
                                        onChange={(e) => handleFormChange('code', e.target.value)}
                                        onBlur={formik.handleBlur}
                                        disabled={modalMode === "edit"}
                                        isInvalid={formik.touched.code && !!formik.errors.code}
                                        style={modalMode === "edit" ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed', opacity: 0.7 } : {}}
                                    />
                                    {formik.touched.code && formik.errors.code && (
                                        <small className="text-danger">{formik.errors.code}</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <FormLabel>Type of Discount {modalMode === "edit" && <span className="text-muted small">(Read-only)</span>}</FormLabel>
                                    <div className="d-flex gap-3 mt-2" style={modalMode === "edit" ? { opacity: 0.6 } : {}}>
                                        <Form.Check
                                            type="radio"
                                            id="fixed-price"
                                            name="discountType"
                                            label="Fixed Price"
                                            value="fixed"
                                            checked={formik.values.discountType === 'fixed'}
                                            onChange={formik.handleChange}
                                            disabled={modalMode === "edit"}
                                            style={modalMode === "edit" ? { cursor: 'not-allowed' } : {}}
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="percentage"
                                            name="discountType"
                                            label="Percentage"
                                            value="percentage"
                                            checked={formik.values.discountType === 'percentage'}
                                            onChange={formik.handleChange}
                                            disabled={modalMode === "edit"}
                                            style={modalMode === "edit" ? { cursor: 'not-allowed' } : {}}
                                        />
                                    </div>
                                    {formik.touched.discountType && formik.errors.discountType && (
                                        <small className="text-danger">{formik.errors.discountType}</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <FormLabel>
                                        {formik.values.discountType === 'fixed' ? 'Enter Amount' : 'Enter Percentage'} {modalMode === "edit" && <span className="text-muted small">(Read-only)</span>}
                                    </FormLabel>
                                    <FormControl
                                        type="number"
                                        placeholder={formik.values.discountType === 'fixed' ? 'e.g., 50' : 'e.g., 25'}
                                        name="discount"
                                        value={formik.values.discount}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={modalMode === "edit"}
                                        isInvalid={formik.touched.discount && !!formik.errors.discount}
                                        min="1"
                                        max={formik.values.discountType === 'percentage' ? '100' : undefined}
                                        style={modalMode === "edit" ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed', opacity: 0.7 } : {}}
                                    />
                                    {formik.touched.discount && formik.errors.discount && (
                                        <small className="text-danger">{formik.errors.discount}</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <FormLabel>Plan Type {modalMode === "edit" && <span className="text-muted small">(Read-only)</span>}</FormLabel>
                                    <FormSelect
                                        name="planType"
                                        value={formik.values.planType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={modalMode === "edit"}
                                        isInvalid={formik.touched.planType && !!formik.errors.planType}
                                        style={modalMode === "edit" ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed', opacity: 0.7 } : {}}
                                    >
                                        <option value="monthly">Monthly</option>
                                        <option value="yearly">Yearly</option>
                                    </FormSelect>
                                    {formik.touched.planType && formik.errors.planType && (
                                        <small className="text-danger">{formik.errors.planType}</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <FormLabel>Limit Total Users {modalMode === "edit" && <span className="text-muted small">(Read-only)</span>}</FormLabel>
                                    <FormControl
                                        type="number"
                                        placeholder="e.g., 100"
                                        name="usersAllowed"
                                        value={formik.values.usersAllowed}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        disabled={modalMode === "edit"}
                                        isInvalid={formik.touched.usersAllowed && !!formik.errors.usersAllowed}
                                        style={modalMode === "edit" ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed', opacity: 0.7 } : {}}
                                    />
                                    {formik.touched.usersAllowed && formik.errors.usersAllowed && (
                                        <small className="text-danger">{formik.errors.usersAllowed}</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <FormLabel>Start Date {modalMode === "edit" && <span className="text-muted small">(Read-only)</span>}</FormLabel>
                                    <div className={`custom-date-picker-wrapper${formik.touched.startDate && formik.errors.startDate ? ' is-invalid' : ''}`}>
                                        <DatePicker
                                            value={formik.values.startDate ? new DateObject(formik.values.startDate) : null}
                                            onChange={(date: DateObject | null) => {
                                                formik.setFieldValue('startDate', date ? date.format('YYYY-MM-DD') : '');
                                                formik.setFieldTouched('startDate', true, false);
                                            }}
                                            format="MM/DD/YYYY"
                                            placeholder="Select start date"
                                            disabled={modalMode === "edit"}
                                            containerClassName="w-100"
                                            inputClass={`form-control w-100${modalMode === "edit" ? ' disabled-date-input' : ''}`}
                                            render={(value, openCalendar) => (
                                                <div
                                                    className={`d-flex align-items-center form-control${modalMode === "edit" ? ' disabled-date-input' : ''}`}
                                                    onClick={modalMode !== "edit" ? openCalendar : undefined}
                                                    style={{ cursor: modalMode === "edit" ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <span className={value ? 'text-dark' : 'text-muted'} style={{ flex: 1 }}>
                                                        {value || 'Select start date'}
                                                    </span>
                                                    <img src={CalenderIcon} alt="calendar" style={{ width: '18px', height: '18px' }} />
                                                </div>
                                            )}
                                        />
                                    </div>
                                    {formik.touched.startDate && formik.errors.startDate && (
                                        <small className="text-danger">{formik.errors.startDate}</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <FormLabel>Expiry Date {modalMode === "edit" && <span className="text-muted small">(Read-only)</span>}</FormLabel>
                                    <div className={`custom-date-picker-wrapper${formik.touched.expiryDate && formik.errors.expiryDate ? ' is-invalid' : ''}`}>
                                        <DatePicker
                                            value={formik.values.expiryDate ? new DateObject(formik.values.expiryDate) : null}
                                            onChange={(date: DateObject | null) => {
                                                formik.setFieldValue('expiryDate', date ? date.format('YYYY-MM-DD') : '');
                                                formik.setFieldTouched('expiryDate', true, false);
                                            }}
                                            format="MM/DD/YYYY"
                                            placeholder="Select expiry date"
                                            disabled={modalMode === "edit"}
                                            containerClassName="w-100"
                                            inputClass={`form-control w-100${modalMode === "edit" ? ' disabled-date-input' : ''}`}
                                            minDate={formik.values.startDate ? new DateObject(formik.values.startDate) : undefined}
                                            render={(value, openCalendar) => (
                                                <div
                                                    className={`d-flex align-items-center form-control${modalMode === "edit" ? ' disabled-date-input' : ''}`}
                                                    onClick={modalMode !== "edit" ? openCalendar : undefined}
                                                    style={{ cursor: modalMode === "edit" ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <span className={value ? 'text-dark' : 'text-muted'} style={{ flex: 1 }}>
                                                        {value || 'Select expiry date'}
                                                    </span>
                                                    <img src={CalenderIcon} alt="calendar" style={{ width: '18px', height: '18px' }} />
                                                </div>
                                            )}
                                        />
                                    </div>
                                    {formik.touched.expiryDate && formik.errors.expiryDate && (
                                        <small className="text-danger">{formik.errors.expiryDate}</small>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>
                        {/* <Row>
                            <Col md={12}>
                                <FormGroup className="form-group d-flex justify-content-between align-items-center">
                                    <FormLabel className="mb-0">Status</FormLabel>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className={formik.values.status === 'active' ? 'text-primary fw-bold' : 'text-muted'}>Active</span>
                                        <Form.Check
                                            type="switch"
                                            id="status-switch"
                                            name="status"
                                            checked={formik.values.status === 'active'}
                                            onChange={(e) => formik.setFieldValue('status', e.target.checked ? 'active' : 'inactive')}
                                            className="custom-switch"
                                        />
                                    </div>
                                </FormGroup>
                            </Col>
                        </Row> */}
                        <Row>
                            <Col md={12}>
                                <FormGroup className="form-group">
                                    <div style={modalMode === "edit" ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                                        <Form.Check
                                            type="checkbox"
                                            id="limit-per-user"
                                            label={`Limit Per User ${modalMode === "edit" ? '(Read-only)' : ''}`}
                                            name="multipleUse"
                                            checked={!formik.values.multipleUse}
                                            onChange={(e) => formik.setFieldValue('multipleUse', !e.target.checked)}
                                            disabled={modalMode === "edit"}
                                        />
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                        Restrict this discount code to a single use per unique user.
                                    </small>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        className="main-bttn w-100"
                        onClick={(e) => {
                            e.preventDefault();
                            formik.handleSubmit();
                        }}
                        disabled={loading || (formik.submitCount > 0 && !formik.isValid)}
                        type="button"
                    >
                        {loading ? 'Saving...' : (modalMode === "edit" ? "Update" : "Create")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Export Modal */}
            <ExportModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                onExportExcel={handleExportExcel}
                onExportPdf={handleExportPdf}
                title="Export Referral Discount Data"
            />
        </>
    );
};

export default ReferralDiscount;

